import React, { Component } from "react";
import ProposalConfirm from "../Form/proposalConfirm";
import "../Form/proposalForm.css";

export default class ProposalDetailsModal extends Component {
  componentDidUpdate(prevProps) {
    // Handle body scroll when modal opens/closes
    if (this.props.show !== prevProps.show) {
      if (this.props.show) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "unset";
      }
    }
  }

  componentWillUnmount() {
    document.body.style.overflow = "unset";
  }

  // Parse and normalize all proposal data to match what ProposalConfirm expects
  normalizeProposalData = (proposal) => {
    if (!proposal)
      return { values: {}, fusionSections: null, fusionProposals: null };

    // Determine if this is a fusion proposal
    const isFusion =
      proposal.program && proposal.program.toLowerCase().includes("fusion");

    // Extract account numbers from various possible locations
    let fusionAccountNumbers = [];
    if (proposal["ACCOUNT NUMBERS"]) {
      fusionAccountNumbers = proposal["ACCOUNT NUMBERS"]
        .split(",")
        .map((num) => num.trim())
        .filter((num) => num);
    } else if (
      proposal.fusionAccountNumbers &&
      Array.isArray(proposal.fusionAccountNumbers)
    ) {
      fusionAccountNumbers = proposal.fusionAccountNumbers;
    }

    // Build normalized values object
    const values = {
      userName: proposal.UserName || proposal["user-name"] || "",
      DateTime: proposal.DateTime || proposal["date-time"] || "",
      clientName: proposal["CLIENT NAMES"] || proposal.clientName || "",
      advisorName: proposal["ADVISOR NAME"] || proposal.advisorName || "",
      customTemplate: proposal.customTemplate || "",
      logo: proposal.LOGO || proposal.logo || "",
      program: proposal.program || "",
      assetValue: proposal.assetValue || proposal.VALUE || "",
      incomeNeeds: proposal.incomeNeeds || proposal.INCOME || "",
      riskTolerance:
        proposal["RISK TOLERANCE"] ||
        proposal.RISK ||
        proposal["embedded-excel"] ||
        "",
      optionsTM: proposal.optionsTM || proposal.BOND || "",
      insertType: proposal.insertType || "Fact Sheet",
      papercheck:
        proposal.papercheck !== undefined ? proposal.papercheck : "true",
      othercheck:
        proposal.othercheck !== undefined ? proposal.othercheck : "false",
      fusionAccountNumbers: fusionAccountNumbers,
      isNewFusionClient: fusionAccountNumbers.length === 0,
      // Additional fields for proper display
      logoSize: proposal.logoSize || "",
      logoOffset: proposal.logoOffset || "",
    };

    // Handle registrations - can be string or object
    if (proposal.REGISTRATION !== undefined) {
      values.registrations = proposal.REGISTRATION;
    } else if (proposal.registrations) {
      values.registrations = proposal.registrations;
    } else {
      // Try to extract from form-info if available (only if it's already an array)
      const formInfo = proposal["form-info"];

      if (formInfo && Array.isArray(formInfo)) {
        const regEntry = formInfo.find((item) => item.key === "REGISTRATION");
        if (regEntry) values.registrations = regEntry.value;
      }
      // Skip FormInfo string parsing - it's Python format and not safe to eval
      // All needed data should be available as direct properties anyway
    }

    // Parse fusion sections and proposals if this is a fusion proposal
    let fusionSections = null;
    let fusionProposals = null;

    if (isFusion) {
      // Parse all sections
      fusionSections = this.parseFusionSections(proposal);

      // Parse fusion proposals from sections
      fusionProposals = this.parseFusionProposals(proposal);
    }

    return { values, fusionSections, fusionProposals };
  };

  // Parse fusion sections from stored JSON strings
  parseFusionSections = (proposal) => {
    const sections = [];

    // Helper to safely parse JSON
    const safeParse = (str) => {
      try {
        return typeof str === "string" ? JSON.parse(str) : str;
      } catch (e) {
        console.error("Error parsing JSON:", e);
        return null;
      }
    };

    // Parse each section type
    if (proposal["PLAN BREAKDOWN SECTION"]) {
      const parsed = safeParse(proposal["PLAN BREAKDOWN SECTION"]);
      if (parsed) {
        sections.push({
          key: "PLAN BREAKDOWN SECTION",
          value: JSON.stringify(parsed), // Keep as string for backend compatibility
          type: "sections",
        });
      }
    }

    if (proposal["RISK IMAGE SECTION"]) {
      const parsed = safeParse(proposal["RISK IMAGE SECTION"]);
      if (parsed) {
        sections.push({
          key: "RISK IMAGE SECTION",
          value: JSON.stringify(parsed),
          type: "sections",
        });
      }
    }

    if (proposal["PIE CHART"]) {
      sections.push({
        key: "PIE CHART",
        value: proposal["PIE CHART"],
        type: "piechart",
      });
    }

    if (proposal["PIE CHART SIGNATURE"]) {
      sections.push({
        key: "PIE CHART SIGNATURE",
        value: proposal["PIE CHART SIGNATURE"],
        type: "piechart",
      });
    }

    if (proposal["SIGNATURE SECTION"]) {
      const parsed = safeParse(proposal["SIGNATURE SECTION"]);
      if (parsed) {
        sections.push({
          key: "SIGNATURE SECTION",
          value: JSON.stringify(parsed),
          type: "sections",
        });
      }
    }

    return sections.length > 0 ? { data: { sections } } : null;
  };

  // Parse individual fusion proposals from sections
  parseFusionProposals = (proposal) => {
    const fusionProposals = [];

    // Helper to safely parse JSON
    const safeParse = (str) => {
      try {
        return typeof str === "string" ? JSON.parse(str) : str;
      } catch (e) {
        console.error("Error parsing JSON:", e);
        return null;
      }
    };

    // First try PLAN BREAKDOWN SECTION (most reliable)
    if (proposal["PLAN BREAKDOWN SECTION"]) {
      const planData = safeParse(proposal["PLAN BREAKDOWN SECTION"]);
      if (planData && Array.isArray(planData)) {
        planData.forEach((item) => {
          if (item.name === "PlanBreakdownData" && Array.isArray(item.value)) {
            const proposalData = {
              formData: {
                clientName: proposal["CLIENT NAMES"] || "",
                advisorName: proposal["ADVISOR NAME"] || "",
                logo: proposal.LOGO || "",
                optionsTM: proposal.optionsTM || proposal.BOND || "",
              },
            };

            // Extract fields from the breakdown
            item.value.forEach((field) => {
              if (field.key === "PROGRAM") {
                proposalData.name = field.value;
                proposalData.formData.program = field.value;
              }
              if (field.key === "RISK") {
                proposalData.formData.riskTolerance = field.value;
              }
              if (field.key === "AMOUNT") {
                const amountStr = field.value.replace(/[^0-9.-]+/g, "");
                proposalData.amount = parseFloat(amountStr) || 0;
                proposalData.formData.assetValue = field.value;
              }
              if (field.key === "PERCENTAGE") {
                const pctStr = field.value.replace("%", "");
                proposalData.percentage = parseFloat(pctStr) || 0;
              }
            });

            proposalData.clientName = proposal["CLIENT NAMES"] || "";

            if (proposalData.name) {
              fusionProposals.push(proposalData);
            }
          }
        });
      }
    }

    // Fallback to SIGNATURE SECTION if no proposals found
    if (fusionProposals.length === 0 && proposal["SIGNATURE SECTION"]) {
      const signatureData = safeParse(proposal["SIGNATURE SECTION"]);
      if (signatureData && Array.isArray(signatureData)) {
        signatureData.forEach((item) => {
          if (
            item.name === "SignatureDataWhite" ||
            item.name === "SignatureDataGray"
          ) {
            if (Array.isArray(item.value)) {
              const proposalData = {
                formData: {
                  advisorName: proposal["ADVISOR NAME"] || "",
                  logo: proposal.LOGO || "",
                  optionsTM: proposal.optionsTM || proposal.BOND || "",
                },
              };

              item.value.forEach((field) => {
                if (field.key === "CLIENT NAME") {
                  proposalData.clientName = field.value;
                  proposalData.formData.clientName = field.value;
                }
                if (field.key === "REGISTRATION") {
                  proposalData.formData.registrations = field.value;
                }
                if (field.key === "PROGRAM") {
                  proposalData.name = field.value;
                  proposalData.formData.program = field.value;
                }
                if (field.key === "RISK TOLERANCE") {
                  proposalData.formData.riskTolerance = field.value;
                }
                if (field.key === "VALUE") {
                  proposalData.formData.assetValue = field.value;
                  const amountStr = field.value.replace(/[^0-9.-]+/g, "");
                  proposalData.amount = parseFloat(amountStr) || 0;
                }
                if (field.key === "PERCENTAGE") {
                  const pctStr = field.value.replace("%", "");
                  proposalData.percentage = parseFloat(pctStr) || 0;
                }
              });

              if (proposalData.name) {
                fusionProposals.push(proposalData);
              }
            }
          }
        });
      }
    }

    // For multiple proposals in a fusion, check if we have multiple in SIGNATURE SECTION
    // This handles cases where there are multiple SignatureDataWhite/Gray entries
    if (fusionProposals.length === 1 && proposal["SIGNATURE SECTION"]) {
      const signatureData = safeParse(proposal["SIGNATURE SECTION"]);
      if (signatureData && Array.isArray(signatureData)) {
        const additionalProposals = [];
        let foundMultiple = false;

        signatureData.forEach((item) => {
          if (
            item.name &&
            item.name.startsWith("SignatureData") &&
            !item.name.includes("White") &&
            !item.name.includes("Gray")
          ) {
            // This might be additional proposal data
            foundMultiple = true;
          }
        });

        if (foundMultiple) {
          // Re-parse looking for all SignatureData entries
          fusionProposals.length = 0; // Clear and re-parse

          signatureData.forEach((item) => {
            if (
              item.name &&
              item.name.includes("SignatureData") &&
              Array.isArray(item.value)
            ) {
              const proposalData = {
                formData: {
                  advisorName: proposal["ADVISOR NAME"] || "",
                  logo: proposal.LOGO || "",
                  optionsTM: proposal.optionsTM || proposal.BOND || "",
                },
              };

              let hasData = false;
              item.value.forEach((field) => {
                if (field.key === "PROGRAM" && field.value) {
                  proposalData.name = field.value;
                  proposalData.formData.program = field.value;
                  hasData = true;
                }
                if (field.key === "CLIENT NAME") {
                  proposalData.clientName = field.value;
                  proposalData.formData.clientName = field.value;
                }
                if (field.key === "REGISTRATION") {
                  proposalData.formData.registrations = field.value;
                }
                if (field.key === "RISK TOLERANCE") {
                  proposalData.formData.riskTolerance = field.value;
                }
                if (field.key === "VALUE") {
                  proposalData.formData.assetValue = field.value;
                  const amountStr = field.value.replace(/[^0-9.-]+/g, "");
                  proposalData.amount = parseFloat(amountStr) || 0;
                }
                if (field.key === "PERCENTAGE") {
                  const pctStr = field.value.replace("%", "");
                  proposalData.percentage = parseFloat(pctStr) || 0;
                }
              });

              if (hasData && proposalData.name) {
                fusionProposals.push(proposalData);
              }
            }
          });
        }
      }
    }

    return fusionProposals.length > 0 ? fusionProposals : null;
  };

  render() {
    const { show, onHide, proposal, lists } = this.props;

    if (!show || !proposal) return null;

    // Normalize all data before passing to ProposalConfirm
    const { values, fusionSections, fusionProposals } =
      this.normalizeProposalData(proposal);

    // Create fusionSummary for display (needed to show fusion sections)
    const fusionSummary = fusionProposals
      ? {
          rebalanceType: "Automatic",
          totalProposals: fusionProposals.length,
          proposals: fusionProposals.map((p) => p.name),
        }
      : null;

    return (
      <>
        {/* Modal Backdrop */}
        <div
          className={`modal-backdrop fade ${show ? "show" : ""}`}
          style={{ display: show ? "block" : "none" }}
          onClick={onHide}
        />

        {/* Modal Dialog */}
        <div
          className={`modal fade ${show ? "show" : ""}`}
          tabIndex="-1"
          role="dialog"
          style={{
            display: show ? "block" : "none",
            overflowY: "auto",
          }}
          onClick={onHide}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-scrollable"
            role="document"
            style={{
              maxHeight: "90vh",
              margin: "1.75rem auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content" style={{ maxHeight: "90vh" }}>
              <div className="modal-header">
                <h5 className="modal-title vicus-blue">Proposal Details</h5>
                <button
                  type="button"
                  className="close"
                  onClick={onHide}
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div
                className="modal-body"
                style={{ overflowY: "auto", maxHeight: "calc(90vh - 120px)" }}
              >
                {/* ProposalConfirm receives fully normalized data */}
                <ProposalConfirm
                  values={values}
                  lists={lists || {}}
                  fusionSections={fusionSections}
                  fusionSummary={fusionSummary}
                  fusionProposals={fusionProposals}
                  handles={{ handleDefault: () => {} }}
                  prevStep={() => {}}
                  nextStep={() => {}}
                  viewOnly={true} // Only this flag needed for display purposes
                />
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onHide}
                  style={{
                    paddingLeft: "30px",
                    paddingRight: "30px",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}
